import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { BrandMark } from '../../components/ui/Icon.js';

interface Entry { required: boolean; until: string | null }

/**
 * Platform and MSP staff entering a client's console (owner, 3 October 2026): the same step
 * whether they pressed the account's button or typed /manage/<account>. They confirm it is them,
 * may say why, and the visit goes into the account's own audit log (API consoleentry.ts). An
 * account's own users never see this. The API's scope check on every call is unchanged; this
 * makes the visit deliberate and visible to the client.
 */
export function ConsoleGate({ tenantId, accountName, staff, children }: {
  tenantId: string; accountName: string | undefined;
  /** False for the account's own users: they are never asked. */
  staff: boolean;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const entry = useQuery({
    queryKey: ['console-entry', tenantId],
    queryFn: () => api.get<Entry>(`/v1/tenants/${tenantId}/console-entry`),
    enabled: !!tenantId && staff,
    staleTime: 60_000,
  });
  // An entry runs out while the page is open: ask again then, not on the next reload.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60_000); return () => clearInterval(t); }, []);

  const step = useStepUp();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const e = entry.data;
  const open = !staff || (!!e && (!e.required || (!!e.until && Date.parse(e.until) > now)));
  if (open) return <>{children}</>;
  if (!e && !entry.error) return null;

  const enter = async () => {
    setBusy(true); setError(null);
    try {
      // The cache is set by the action itself: after the password check it is replayed, not returned here.
      await step.run(async () => {
        const r = await api.post<Entry>(`/v1/tenants/${tenantId}/console-entry`, reason.trim() ? { reason: reason.trim() } : {});
        qc.setQueryData(['console-entry', tenantId], r);
        return r;
      });
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div className="arc-content">
      <div className="arc-page-max" style={{ maxWidth: 480, paddingTop: 64 }}>
        <div className="arc-card arc-card-body arc-stack" style={{ gap: 12 }}>
          <div className="arc-row" style={{ gap: 10 }}>
            <BrandMark size={22} />
            <h1 className="arc-h1" style={{ margin: 0, fontSize: 20 }}>Open {accountName ?? 'this account'}’s console</h1>
          </div>
          <p className="arc-muted" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>
            You are opening a client’s TMS as staff. The visit is recorded in the account’s audit log, where
            its own admins can see it, and lasts 8 hours.
          </p>
          {(entry.error || !!error) && <ErrorBanner error={(entry.error ?? error) as Error} />}
          <StepUpPrompt step={step} what="Opening a client’s console" />
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="ce-reason">Reason</label>
            <input id="ce-reason" className="arc-input" maxLength={200} value={reason}
              onChange={(ev) => setReason(ev.target.value)} placeholder="Optional: a ticket, or what you are checking" />
            <p className="arc-hint">Shown with the visit in the audit log.</p>
          </div>
          <div className="arc-row" style={{ gap: 8 }}>
            <button type="button" className="arc-btn arc-btn-primary" disabled={busy || step.challenge} onClick={() => void enter()}>
              {busy ? 'Opening…' : 'Open the console'}
            </button>
            <a className="arc-btn" href="/accounts">All accounts</a>
          </div>
        </div>
      </div>
    </div>
  );
}
